import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { GAME_FONT, type PhoneMetrics } from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface PhoneReferFriendDeps {
  drawCrateIcon(x: number, y: number, size: number): void;
  drawGemIcon(x: number, y: number, size: number): void;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawPhoneReferFriendScreen(
  deps: PhoneReferFriendDeps,
  ctx: CanvasRenderingContext2D,
  model: RenderModel,
  uiRects: UiRects,
  scrX: number, scrY: number, scrW: number, scrH: number,
  contentX: number, contentW: number, _contentRight: number,
  metrics: PhoneMetrics,
): void {
  const desktop = metrics.desktop;
  let curY = scrY + metrics.topPad;

  // Title
  ctx.fillStyle = "#a78bfa";
  ctx.font = `bold ${metrics.titleFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("REFER A FRIEND", scrX + scrW / 2, curY);
  curY += metrics.titleGap + 6;

  // Crate icon centered
  const iconSize = desktop ? 48 : 39;
  deps.drawCrateIcon(scrX + (scrW - iconSize) / 2, curY, iconSize);
  curY += iconSize + (desktop ? 14 : 10);

  // Description lines
  const descLineH = desktop ? 14 : 12;
  const descLines = [
    "INVITE YOUR FRIENDS TO",
    "PLAY BITBEAST AND YOU",
    "BOTH GET REWARDS!",
  ];
  ctx.fillStyle = "#c8d8f0";
  ctx.font = `${metrics.labelFontPx}px ${GAME_FONT}`;
  for (const line of descLines) {
    ctx.fillText(line, scrX + scrW / 2, curY);
    curY += descLineH;
  }
  curY += 12;

  // Rewards section
  ctx.fillStyle = "#ffd700";
  ctx.font = `bold ${metrics.sectionFontPx}px ${GAME_FONT}`;
  ctx.fillText("REWARDS", scrX + scrW / 2, curY);
  curY += desktop ? 16 : 14;

  const rewards = [
    { label: "YOU GET", value: "50 GEMS", color: "#4dff88" },
    { label: "FRIEND GETS", value: "50 GEMS", color: "#64d8ff" },
  ];
  const rewardRowH = desktop ? 28 : 26;
  const gemIconSize = desktop ? 14 : 12;
  for (const r of rewards) {
    ctx.fillStyle = "rgba(22,32,48,0.95)";
    ctx.fillRect(contentX, curY, contentW, rewardRowH);
    ctx.strokeStyle = r.color + "44";
    ctx.lineWidth = 1;
    ctx.strokeRect(contentX, curY, contentW, rewardRowH);

    ctx.fillStyle = "#7fa0c2";
    ctx.font = `${metrics.helperFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(r.label, contentX + 8, curY + rewardRowH / 2);

    // Gem icon + value text
    ctx.fillStyle = r.color;
    ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "right";
    const valueTextW = ctx.measureText(r.value).width;
    const valueRightEdge = contentX + contentW - 8;
    ctx.fillText(r.value, valueRightEdge, curY + rewardRowH / 2);
    deps.drawGemIcon(
      valueRightEdge - valueTextW - gemIconSize - 4,
      curY + (rewardRowH - gemIconSize) / 2,
      gemIconSize,
    );

    curY += rewardRowH + 6;
  }
  curY += 10;

  // How it works
  ctx.fillStyle = "#7fa0c2";
  ctx.font = `${metrics.metaFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  const howLineH = desktop ? 14 : 12;
  const howLines = [
    "1. SHARE YOUR LINK",
    "2. FRIEND SIGNS UP & PLAYS",
    "3. YOU BOTH GET GEMS!",
  ];
  for (const line of howLines) {
    ctx.fillText(line, scrX + scrW / 2, curY);
    curY += howLineH;
  }

  // ── "LINK COPIED!" indicator ──
  if (model.friendsError && model.friendsError.includes("COPIED")) {
    curY += 8;
    ctx.fillStyle = "#4dff88";
    ctx.font = `bold ${metrics.labelFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(model.friendsError, scrX + scrW / 2, curY);
  }

  // ── Bottom buttons (pinned) ──
  const btnH = metrics.ctaH;
  const btnGap = 4;

  // BACK — full width at very bottom
  const backY = scrY + scrH - btnH - 6;
  const backRect: Rect = { x: contentX, y: backY, w: contentW, h: btnH };
  uiRects.phoneReferBack = backRect;
  ctx.fillStyle = "#162030";
  ctx.fillRect(backRect.x, backRect.y, backRect.w, backRect.h);
  ctx.strokeStyle = "#5a7a9a";
  ctx.lineWidth = 1;
  ctx.strokeRect(backRect.x, backRect.y, backRect.w, backRect.h);
  ctx.fillStyle = "#8a9ab0";
  ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("BACK", backRect.x + backRect.w / 2, backRect.y + backRect.h / 2);

  // SHARE + COPY — side by side above BACK
  const pairY = backY - btnH - btnGap;
  const halfW = Math.floor((contentW - btnGap) / 2);

  const shareRect: Rect = { x: contentX, y: pairY, w: halfW, h: btnH };
  uiRects.phoneReferShare = shareRect;
  ctx.fillStyle = "#0a2a10";
  ctx.fillRect(shareRect.x, shareRect.y, shareRect.w, shareRect.h);
  ctx.strokeStyle = "#4dff88";
  ctx.lineWidth = 1;
  ctx.strokeRect(shareRect.x, shareRect.y, shareRect.w, shareRect.h);
  ctx.fillStyle = "#4dff88";
  ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("SHARE", shareRect.x + shareRect.w / 2, shareRect.y + shareRect.h / 2);

  // COPY LINK button — changes to "COPIED!" when active
  const isCopied = !!(model.friendsError && model.friendsError.includes("COPIED"));
  const copyRect: Rect = { x: contentX + halfW + btnGap, y: pairY, w: halfW, h: btnH };
  uiRects.phoneReferCopy = copyRect;
  ctx.fillStyle = isCopied ? "#0a2a10" : "#1a1a30";
  ctx.fillRect(copyRect.x, copyRect.y, copyRect.w, copyRect.h);
  ctx.strokeStyle = isCopied ? "#4dff88" : "#a78bfa";
  ctx.lineWidth = 1;
  ctx.strokeRect(copyRect.x, copyRect.y, copyRect.w, copyRect.h);
  ctx.fillStyle = isCopied ? "#4dff88" : "#a78bfa";
  ctx.fillText(isCopied ? "COPIED!" : "COPY LINK", copyRect.x + copyRect.w / 2, copyRect.y + copyRect.h / 2);
}
